import { useId, type CSSProperties, type ReactNode } from "react";
import { Avatar, Dialog } from "../../components";
import { playArt, type ArtKind } from "../art";
import { playerName, type PlayProps } from "../types";
import { PlayerChoices } from "./PlayerChoices";
import { publicTableModel } from "../public-presentation";
import { TableRoster } from "./TableRoster";
import { SubmitButton } from "./ActionStatus";
import { useDraft } from "./drafts";
import { GameIcon } from "./GameIcons";
export { SubmitButton } from "./ActionStatus";
export { draftScope } from "./drafts";
/** Cover art, theme-switched in CSS. "band" is the poster strip used when the
 * phase has room; "stamp" is a rounded-square crop for dense phases. Purely
 * decorative: never carries state. */
export function GameArt({
  kind,
  variant = "band",
}: {
  kind: ArtKind;
  variant?: "band" | "stamp";
}) {
  const art = playArt[kind];
  return (
    <div
      className={`play-art play-art-${kind} is-${variant}`}
      aria-hidden="true"
      style={
        {
          "--art-light": `url(${art.light})`,
          "--art-dark": `url(${art.dark})`,
        } as CSSProperties
      }
    />
  );
}
/** Chip used in context headers and boards. */
export function Chip({
  children,
  tone = "plain",
  className = "",
}: {
  children: ReactNode;
  tone?: "plain" | "accent" | "field";
  className?: string;
}) {
  return (
    <span className={`play-chip is-${tone}${className ? " " + className : ""}`}>
      {children}
    </span>
  );
}
/** The private card face: role, secret and supporting notes. Rendered only inside
 * the deliberate private surface (dialog or inline), never on a public board. */
export function PrivateInfo({
  state,
  children,
}: Pick<PlayProps, "state"> & { children?: ReactNode }) {
  const s = state.game.private,
    p = state.game.public,
    n = (id: string) => playerName(p.players ?? [], id);
  const notes: ReactNode[] = [];
  if (s.mafiaPlayerIds?.length)
    notes.push(
      <p key="team">
        <span>Your team</span>
        {s.mafiaPlayerIds.map(n).join(", ")}
      </p>,
    );
  if (s.ownNightSelection)
    notes.push(
      <p key="own">
        <span>
          {state.game.phase === "night"
            ? "Night choice locked"
            : "Last night choice"}
        </span>
        {s.ownNightSelection.targetId
          ? n(s.ownNightSelection.targetId)
          : "Abstained"}
      </p>,
    );
  s.mafiaSelections?.forEach((v: any) =>
    notes.push(
      <p key={`sel-${v.playerId}`}>
        <span>{n(v.playerId)} selected</span>
        {v.targetId ? n(v.targetId) : "No target"}
      </p>,
    ),
  );
  s.knownRoles?.forEach((v: any) =>
    notes.push(
      <p key={`known-${v.playerId}`} className="is-role">
        <span>{n(v.playerId)}</span>
        {String(v.role).replaceAll("-", " ")}
      </p>,
    ),
  );
  if (s.investigations?.length)
    notes.push(
      <section key="inv" className="private-investigations">
        <h4>Your investigations</h4>
        {s.investigations.map((v: any, i: number) => (
          <p key={i} className="is-role">
            <span>{n(v.targetId)}</span>
            {v.faction ?? v.party}
          </p>
        ))}
        {s.investigationsOmitted ? (
          <p>{s.investigationsOmitted} older investigations omitted.</p>
        ) : null}
      </section>,
    );
  if (s.peeks?.length)
    notes.push(
      <p key="peek" className="is-role">
        <span>Last policy peek</span>
        {s.peeks.at(-1).policies.join(" · ")}
      </p>,
    );
  return (
    <div className="private-card-face">
      {s.role ? (
        <div className="private-block">
          <span className="private-label">Your role</span>
          <h3 className="private-role">{s.role.replaceAll("-", " ")}</h3>
        </div>
      ) : null}
      {s.word ? (
        <div className="private-block">
          <span className="private-label">Your word</span>
          <strong className="secret-word">{s.word}</strong>
        </div>
      ) : null}
      {s.locationId ? (
        <div className="private-block">
          <span className="private-label">Your location</span>
          <strong className="secret-word">
            {p.locations?.find((l: any) => l.id === s.locationId)?.name ??
              "Unknown location"}
          </strong>
        </div>
      ) : null}
      {s.target !== null && s.target !== undefined ? (
        <div className="private-block">
          <span className="private-label">Secret target</span>
          <strong className="secret-word">
            {s.target}
            <small>/100</small>
          </strong>
          <div className="private-scale" data-tour="private-scale">
            <span>{p.scale?.left}</span>
            <div className="private-scale-bar">
              <i style={{ left: `${s.target}%` }} />
            </div>
            <span>{p.scale?.right}</span>
          </div>
        </div>
      ) : null}
      {children}
      {notes.length ? <div className="private-notes">{notes}</div> : null}
    </div>
  );
}
export function TargetAction({
  players,
  ids,
  type,
  label,
  emptyLabel = "Pick a player",
  send,
  allowAbstain = false,
  confirm = false,
  scope,
  offlineIds,
}: {
  players: any[];
  ids: string[];
  type: string;
  label: string;
  /** Names the missing step while nothing is selected. */
  emptyLabel?: string;
  send: PlayProps["send"];
  allowAbstain?: boolean;
  confirm?: boolean;
  /** Draft scope (seat, match, phase): the choice survives pause and remounts. */
  scope: string;
  offlineIds?: readonly string[];
}) {
  const [stored, setChoice] = useDraft<string | null>(scope, `${type}:target`, null),
    [sure, setSure] = useDraft(scope, `${type}:confirm`, false);
  // A choice that is no longer eligible is not silently kept.
  const choice =
    stored === "abstain"
      ? allowAbstain
        ? stored
        : null
      : stored && ids.includes(stored)
        ? stored
        : null;
  const submit = () =>
    send({ type, targetId: choice === "abstain" ? null : choice });
  return (
    <>
      <PlayerChoices
        players={players}
        eligibleIds={ids}
        selectedId={choice}
        onSelect={(id) => {
          setChoice(id);
          setSure(false);
        }}
        allowAbstain={allowAbstain}
        offlineIds={offlineIds}
      />
      {sure && choice ? (
        <Dialog title="Confirm execution" onClose={() => setSure(false)}>
          <p className="play-confirm-copy">
            Eliminate <strong>{playerName(players, choice)}</strong>? This
            cannot be undone.
          </p>
          <SubmitButton type="button" onClick={submit}>
            Confirm execution
          </SubmitButton>
          <button className="text-button" onClick={() => setSure(false)}>
            Cancel
          </button>
        </Dialog>
      ) : null}
      <SubmitButton
        type="button"
        disabled={choice === null}
        onClick={() => (confirm ? setSure(true) : submit())}
      >
        {choice === null ? emptyLabel : label}
      </SubmitButton>
    </>
  );
}
export function TextAction({
  label,
  type,
  placeholder,
  send,
  scope,
  validate,
  submitLabel,
}: {
  label: string;
  type: string;
  placeholder: string;
  send: PlayProps["send"];
  /** Draft scope (seat, match, phase): the text survives pause and remounts. */
  scope: string;
  /** Local check before sending; returns the message to show under the field. */
  validate?: (text: string) => string | null;
  submitLabel?: string;
}) {
  const [text, setText] = useDraft(scope, `${type}:text`, "");
  const hintId = useId();
  const problem = text.trim() && validate ? validate(text) : null;
  return (
    <form
      className="play-text-action"
      data-tour="text-action"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim() && !problem) send({ type, text: text.trim() });
      }}
    >
      <label>
        <span className="play-field-label">{label}</span>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={type === "guess" ? 48 : 160}
          placeholder={placeholder}
          autoComplete="off"
          required
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? hintId : undefined}
        />
      </label>
      {problem ? (
        <p className="play-field-hint is-error" id={hintId}>
          {problem}
        </p>
      ) : null}
      <SubmitButton disabled={!text.trim() || Boolean(problem)}>
        {submitLabel ?? (type === "guess" ? "Submit final guess" : "Send clue")}
      </SubmitButton>
    </form>
  );
}
export function TableProgress({ state }: Pick<PlayProps, "state">) {
  return (
    <TableRoster
      model={publicTableModel(state.game, state.players ?? [])}
      viewerId={state.playerId}
    />
  );
}
/** Mid-game eliminated-role reveals (Mafia option) stay in People; the finished
 * table shows every role inline (see RoleGrid). */
export function FinishedRoles({ state }: Pick<PlayProps, "state">) {
  const p = state.game.public;
  return p.revealedRoles?.length && !state.game.finished ? (
    <p className="play-board-caption">Revealed roles are listed in People.</p>
  ) : null;
}
const roleNames: Record<string, string> = {
  hitler: "Hitler",
  liberal: "Liberal",
  fascist: "Fascist",
  mafia: "Mafia",
  doctor: "Doctor",
  detective: "Detective",
  villager: "Villager",
};
/** Finished Mafia / Secret Hitler table: every seat with its revealed role, from
 * public `revealedRoles` (all public once the match ends). */
export function RoleGrid({
  state,
  viewerId,
}: Pick<PlayProps, "state"> & { viewerId: string | null }) {
  const p = state.game.public,
    players = p.players ?? [],
    roles: { playerId: string; role: string }[] = p.revealedRoles ?? [];
  if (!roles.length) return null;
  const winners: string[] =
    (state.game.winner as { playerIds?: string[] } | undefined)?.playerIds ??
    [];
  return (
    <section
      className="play-role-grid"
      aria-label="Everyone’s role"
      data-tour="roles"
    >
      <h2 className="play-board-heading">Everyone’s role</h2>
      <ul className={players.length > 8 ? "is-dense" : undefined}>
        {players.map((v: any, i: number) => {
          const role = roles.find((r) => r.playerId === v.id)?.role;
          if (!role) return null;
          return (
            <li
              key={v.id}
              className={`${role === "hitler" ? "is-hitler " : ""}${v.id === viewerId ? "is-you " : ""}${winners.includes(v.id) ? "is-winner" : ""}`}
            >
              <Avatar name={v.name} index={i} small />
              <span className="role-name">
                <strong>{playerName(players, v.id)}</strong>
                <span className="role-line">
                  <span className={`play-chip is-role role-${role}`}>
                    {roleNames[role] ?? role.replaceAll("-", " ")}
                  </span>
                  {v.id === viewerId ? (
                    <span className="seat-you">You</span>
                  ) : null}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
/** "You won — …" for the viewer's own device. */
export function PersonalOutcomeLine({
  outcome,
}: {
  outcome: { text: string; won: boolean } | null;
}) {
  return outcome ? (
    <p
      className={`play-personal-outcome ${outcome.won ? "is-win" : "is-loss"}`}
      data-tour="personal-outcome"
    >
      <GameIcon name={outcome.won ? "victory" : "close"} size={20} />
      {outcome.text}
    </p>
  ) : null;
}
/** Completed public ballots grouped by target, most votes first: legible on a
 * phone and from across a room. */
export function BallotTally({
  players,
  ballots,
  viewerId,
  heading = "How everyone voted",
  compact = false,
}: {
  players: any[];
  ballots: { playerId: string; targetId: string | null }[];
  viewerId?: string | null;
  heading?: string;
  /** Beside a full evidence grid: a strip of targets and counts on phones. */
  compact?: boolean;
}) {
  if (!ballots?.length) return null;
  const n = (id: string | null) =>
    id === viewerId ? "You" : playerName(players, id);
  const groups = new Map<string | null, string[]>();
  for (const b of ballots)
    groups.set(b.targetId ?? null, [
      ...(groups.get(b.targetId ?? null) ?? []),
      b.playerId,
    ]);
  const rows = [...groups.entries()].sort(
    (a, b) =>
      b[1].length - a[1].length || (a[0] === null ? 1 : b[0] === null ? -1 : 0),
  );
  return (
    <section
      className={`play-tally${compact ? " is-compact" : ""}`}
      data-tour="tally"
      aria-label={heading}
    >
      <h2 className="play-board-heading">{heading}</h2>
      <ul>
        {rows.map(([target, voters]) => (
          <li key={target ?? "abstain"}>
            <span className="tally-target">
              <strong>{target ? n(target) : "Abstained"}</strong>
              <span className="tally-count">
                {voters.length} {voters.length === 1 ? "vote" : "votes"}
              </span>
            </span>
            <small>{voters.map((v) => n(v)).join(", ")}</small>
          </li>
        ))}
      </ul>
    </section>
  );
}
